import { useFormikContext } from 'formik';
import {
    GenericObject,
    checkEmail,
    checkIfSerbianPhoneNumber,
    checkPhoneNumber,
    checkSerbianBankAccount,
    checkSerbianIBAN,
    checkUCN,
    checkIfAdult,
    checkUCNWithBirthDate,
    constValues,
} from '@fnol/library/src/lib';
import { bankType, personType, riskTypeLife, translations } from './consts';

export const validateIndividual = (
    type: personType,
    id?: number | string,
): {
    errors: { [key: string]: string };
    touched: { [key: string]: boolean };
    isValid: boolean;
} => {
    const individualExistingErrors: { [key: string]: string } = {};
    const individualTouched: { [key: string]: boolean } = {};
    let individualIsValid = true;

    const { values } = useFormikContext<GenericObject>();
    const idIndividual = id ? id : '';

    const name = `${personType[type]}${idIndividual}Name`;
    const lastname = `${personType[type]}${idIndividual}LastName`;
    const dateOfBirth = `${personType[type]}${idIndividual}DateOfBirth`;
    const ucn = `${personType[type]}${idIndividual}UCN`;
    const citizenship = `${personType[type]}${idIndividual}Citizenship`;
    const country = `${personType[type]}${idIndividual}Country`;
    const city = `${personType[type]}${idIndividual}City`;
    const cityInSerbia = `${personType[type]}${idIndividual}CityInSerbia`;
    const zipCode = `${personType[type]}${idIndividual}ZipCode`;
    const street = `${personType[type]}${idIndividual}Street`;
    const municipality = `${personType[type]}${idIndividual}Municipality`;
    const email = `${personType[type]}${idIndividual}Email`;
    const phone = `${personType[type]}${idIndividual}Phone`;
    const duplicatedUcn = `${personType[type]}${idIndividual}DuplicatedUcn`;

    const currentResidenceAddress = `${personType[type]}${idIndividual}CurrentResidenceAddress`;
    const currentResidenceStreet = `${personType[type]}${idIndividual}CurrentResidenceStreet`;
    const currentResidenceMunicipality = `${personType[type]}${idIndividual}CurrentResidenceMunicipality`;
    const currentResidenceCity = `${personType[type]}${idIndividual}CurrentResidenceCity`;
    const currentResidenceCityInSerbia = `${personType[type]}${idIndividual}CurrentResidenceCityInSerbia`;
    const currentResidenceZipCode = `${personType[type]}${idIndividual}CurrentResidenceZipCode`;

    const mailingAddress = `${personType[type]}${idIndividual}MailingAddress`;
    const mailingStreet = `${personType[type]}${idIndividual}MailingStreet`;
    const mailingMunicipality = `${personType[type]}${idIndividual}MailingMunicipality`;
    const mailingCity = `${personType[type]}${idIndividual}MailingCity`;
    const mailingCityInSerbia = `${personType[type]}${idIndividual}MailingCityInSerbia`;
    const mailingZipCode = `${personType[type]}${idIndividual}MailingZipCode`;

    const docType = `${personType[type]}${idIndividual}PersonalDocumentType`;
    const docNumber = `${personType[type]}${idIndividual}PersonalDocumentNumber`;
    const issuePlace = `${personType[type]}${idIndividual}DocumentIssuePlace`;
    const issueDate = `${personType[type]}${idIndividual}DocumentIssueDate`;
    const placeOfBirth = `${personType[type]}${idIndividual}PlaceOfBirth`;
    const gender = `${personType[type]}${idIndividual}Gender`;

    const payment = `paymentToUser${idIndividual}`;
    const bank = `${personType[type]}${idIndividual}Bank`;
    const bankName = `${personType[type]}${idIndividual}BankName`;
    const accountNumber = `${personType[type]}${idIndividual}AccountNumber`;
    const attorneySignature = `${personType[type]}${idIndividual}AttorneySignature`;

    const statement = `${personType[type]}${idIndividual}Statement`;
    const statementNote = `${personType[type]}${idIndividual}StatementNote`;

    individualTouched[name] = true;
    individualTouched[lastname] = true;
    individualTouched[dateOfBirth] = true;
    individualTouched[ucn] = true;
    individualTouched[citizenship] = true;
    individualTouched[country] = true;
    individualTouched[city] = true;
    individualTouched[cityInSerbia] = true;
    individualTouched[zipCode] = true;
    individualTouched[street] = true;
    individualTouched[municipality] = true;
    individualTouched[email] = true;
    individualTouched[phone] = true;

    individualTouched[currentResidenceAddress] = true;
    individualTouched[currentResidenceStreet] = true;
    individualTouched[currentResidenceMunicipality] = true;
    individualTouched[currentResidenceCity] = true;
    individualTouched[currentResidenceCityInSerbia] = true;
    individualTouched[currentResidenceZipCode] = true;

    individualTouched[mailingAddress] = true;
    individualTouched[mailingStreet] = true;
    individualTouched[mailingMunicipality] = true;
    individualTouched[mailingCity] = true;
    individualTouched[mailingCityInSerbia] = true;
    individualTouched[mailingZipCode] = true;

    individualTouched[docType] = true;
    individualTouched[docNumber] = true;
    individualTouched[issuePlace] = true;
    individualTouched[issueDate] = true;
    individualTouched[placeOfBirth] = true;
    individualTouched[gender] = true;
    individualTouched[payment] = true;
    individualTouched[bank] = true;
    individualTouched[bankName] = true;
    individualTouched[accountNumber] = true;
    individualTouched[attorneySignature] = true;

    individualTouched[statement] = true;
    individualTouched[statementNote] = true;

    if (
        (values[payment] === 'false' && type === personType.thirdPerson) ||
        (!values[payment] && type !== personType.thirdPerson)
    ) {
        if (!values[name]) {
            individualExistingErrors[name] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[lastname]) {
            individualExistingErrors[lastname] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[dateOfBirth]) {
            individualExistingErrors[dateOfBirth] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[municipality]) {
            individualExistingErrors[municipality] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[currentResidenceAddress]) {
            individualExistingErrors[currentResidenceAddress] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (values[currentResidenceAddress] === 'false') {
            if (!values[currentResidenceStreet]) {
                individualExistingErrors[currentResidenceStreet] = translations.validationMessages.REQUIRED_FIELD;
                individualIsValid = false;
            }

            if (!values[currentResidenceMunicipality]) {
                individualExistingErrors[currentResidenceMunicipality] = translations.validationMessages.REQUIRED_FIELD;
                individualIsValid = false;
            }

            if (
                values[country] !== constValues.countrySerbiaId.toString(10) &&
                values[country] !== constValues.countrySerbiaId &&
                values[currentResidenceCity] === ''
            ) {
                individualExistingErrors[currentResidenceCity] = translations.validationMessages.REQUIRED_FIELD;
                individualIsValid = false;
            }

            if (
                (values[country] === constValues.countrySerbiaId.toString(10) ||
                    values[country] === constValues.countrySerbiaId) &&
                (!values[currentResidenceCityInSerbia] || values[currentResidenceCityInSerbia].id === '')
            ) {
                individualExistingErrors[currentResidenceCityInSerbia] = translations.validationMessages.REQUIRED_FIELD;
                individualIsValid = false;
            }

            if (!values[currentResidenceZipCode]) {
                individualExistingErrors[currentResidenceZipCode] = translations.validationMessages.REQUIRED_FIELD;
                individualIsValid = false;
            }
        }

        if (!values[mailingAddress]) {
            individualExistingErrors[mailingAddress] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (values[mailingAddress] === 'false') {
            if (!values[mailingStreet]) {
                individualExistingErrors[mailingStreet] = translations.validationMessages.REQUIRED_FIELD;
                individualIsValid = false;
            }

            if (!values[mailingMunicipality]) {
                individualExistingErrors[mailingMunicipality] = translations.validationMessages.REQUIRED_FIELD;
                individualIsValid = false;
            }

            if (
                values[country] !== constValues.countrySerbiaId.toString(10) &&
                values[country] !== constValues.countrySerbiaId &&
                values[mailingCity] === ''
            ) {
                individualExistingErrors[mailingCity] = translations.validationMessages.REQUIRED_FIELD;
                individualIsValid = false;
            }

            if (
                (values[country] === constValues.countrySerbiaId.toString(10) ||
                    values[country] === constValues.countrySerbiaId) &&
                (!values[mailingCityInSerbia] || values[mailingCityInSerbia].id === '')
            ) {
                individualExistingErrors[mailingCityInSerbia] = translations.validationMessages.REQUIRED_FIELD;
                individualIsValid = false;
            }

            if (!values[mailingZipCode]) {
                individualExistingErrors[mailingZipCode] = translations.validationMessages.REQUIRED_FIELD;
                individualIsValid = false;
            }
        }

        // if (values[dateOfBirth] && !checkIfAdult(values[dateOfBirth])) {
        //     individualExistingErrors[dateOfBirth] = translations.validationMessages.NOT_ADULT;
        //     individualIsValid = false;
        // }

        if (
            !values[ucn] &&
            (values[country] === constValues.countrySerbiaId ||
                values[country] === constValues.countrySerbiaId.toString(10))
        ) {
            individualExistingErrors[ucn] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (
            values[dateOfBirth] &&
            values[ucn] &&
            checkIfAdult(values[dateOfBirth]) &&
            (values[country] === constValues.countrySerbiaId ||
                values[country] === constValues.countrySerbiaId.toString(10)) &&
            !checkUCNWithBirthDate(values[dateOfBirth], values[ucn])
        ) {
            individualExistingErrors[dateOfBirth] =
                translations.validationMessages.UNIQUE_CITIZEN_NUMBER_AND_BIRTH_NO_NOT_EQUAL;
            individualIsValid = false;
        }

        if (
            values[ucn] &&
            values.reportedClaims &&
            values.reportedClaims.find((item: any) => item.jmbg === values[ucn])
        ) {
            individualExistingErrors[ucn] = translations.validationMessages.INSURED_CASE_ALREADY_EXIST;
            individualIsValid = false;
        }

        if (!values[citizenship]) {
            individualExistingErrors[citizenship] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (
            values[country] === constValues.countrySerbiaId ||
            values[country] === constValues.countrySerbiaId.toString(10)
        ) {
            if (values[ucn] && !checkUCN(values[ucn])) {
                individualExistingErrors[ucn] = translations.validationMessages.UCN_INVALID_FORMAT;
                individualIsValid = false;
            } else {
                if (values[ucn] && values[duplicatedUcn]) {
                    individualExistingErrors[ucn] = ' ';
                    individualIsValid = false;
                }
            }
        }

        if (values[country] === '') {
            individualExistingErrors[country] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (
            values[country] !== constValues.countrySerbiaId.toString(10) &&
            values[country] !== constValues.countrySerbiaId &&
            values[city] === ''
        ) {
            individualExistingErrors[city] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (
            (values[country] === constValues.countrySerbiaId.toString(10) ||
                values[country] === constValues.countrySerbiaId) &&
            (!values[cityInSerbia] || values[cityInSerbia].id === '')
        ) {
            individualExistingErrors[cityInSerbia] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (values[zipCode] === '') {
            individualExistingErrors[zipCode] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (values[street] === '') {
            individualExistingErrors[street] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[email]) {
            individualExistingErrors[email] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (values[email] && !checkEmail(values[email])) {
            // check only if there is a value
            individualExistingErrors[email] = translations.validationMessages.EMAIL_ADDRESS_FORMAT;
            individualIsValid = false;
        }
        if (!values[phone] || (values[phone] && values[phone].length < 5)) {
            individualExistingErrors[phone] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (
            (values[phone] && values[phone].length > 4 && !checkPhoneNumber(values[phone])) ||
            (values[phone] &&
                values[phone].length > 4 &&
                values[country] === constValues.countrySerbiaId &&
                !checkIfSerbianPhoneNumber(values[phone]))
        ) {
            individualExistingErrors[phone] = translations.validationMessages.PHONE_NUMBER_FORMAT;
            individualIsValid = false;
        }

        if (!values[docType]) {
            individualExistingErrors[docType] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[docNumber]) {
            individualExistingErrors[docNumber] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[issuePlace]) {
            individualExistingErrors[issuePlace] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[issueDate]) {
            individualExistingErrors[issueDate] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[placeOfBirth]) {
            individualExistingErrors[placeOfBirth] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[gender]) {
            individualExistingErrors[gender] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[statement]) {
            individualExistingErrors[statement] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[statementNote] && values[statement] === 'false') {
            individualExistingErrors[statementNote] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }
    }

    if (
        ((!values.risks.includes(riskTypeLife.outlive) &&
            !values.risks.includes(riskTypeLife.purchase) &&
            values[payment] !== undefined &&
            ((values[payment] === 'true' && type === personType.user) ||
                (values[payment] === 'true' && type === personType.thirdPerson))) ||
            type === personType.thirdPerson) &&
        (values.inputPersonOpen == false || !values.inputPersonOpen)
    ) {
        if (!values[bank]) {
            individualExistingErrors[bank] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[bankName]) {
            individualExistingErrors[bankName] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[accountNumber]) {
            individualExistingErrors[accountNumber] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        } else if (values[bank] === bankType.domestic) {
            if (!checkSerbianBankAccount(values[accountNumber])) {
                individualExistingErrors[accountNumber] = translations.validationMessages.ACCOUNT_NUMBER_INVALID_FORMAT;
                individualIsValid = false;
            }
        } else if (values[bank] === bankType.iban) {
            if (!checkSerbianIBAN(values[accountNumber])) {
                individualExistingErrors[accountNumber] =
                    translations.validationMessages.ACCOUNT_NUMBER_INVALID_FORMAT_IBAN;
                individualIsValid = false;
            }
        }
    }

    return { errors: individualExistingErrors, touched: individualTouched, isValid: individualIsValid };
};

export const useValidateIndividual = (type: personType, ids?: number[]): any => {
    const validateIndividualData = ids?.length
        ? ids.map((id) => validateIndividual(type, id))
        : validateIndividual(type);

    const individualObj = Array.isArray(validateIndividualData)
        ? validateIndividualData.reduce(
              (acc: any, { errors, isValid, touched }: any) => ({
                  errors: { ...acc.errors, ...errors },
                  isValid: Object.keys(errors).length === 0 && errors.constructor === Object ? true : false,
                  touched: { ...acc.touched, ...touched },
              }),
              { errors: {}, isValid: true, touched: {} },
          )
        : validateIndividualData;

    return individualObj;
};

export const useValidateLegalEntity = (
    type: personType,
): {
    errors: { [key: string]: string };
    touched: { [key: string]: boolean };
    isValid: boolean;
} => {
    const legalEntityExistingErrors: { [key: string]: string } = {};
    const legalEntityTouched: { [key: string]: boolean } = {};
    let legalEntityIsValid = true;

    const { values } = useFormikContext<any>();

    const businessName = `${personType[type]}BusinessName`;
    const country = `${personType[type]}Country`;
    const vat = `${personType[type]}VAT`;
    const establishmentDate = `${personType[type]}EstablishmentDate`;
    const establishmentPlace = `${personType[type]}EstablishmentPlace`;
    const name = `${personType[type]}Name`;
    const lastName = `${personType[type]}LastName`;
    const docType = `${personType[type]}PersonalDocumentType`;
    const docNumber = `${personType[type]}PersonalDocumentNumber`;
    const issuePlace = `${personType[type]}DocumentIssuePlace`;
    const issueDate = `${personType[type]}DocumentIssueDate`;
    const gender = `${personType[type]}Gender`;
    const street = `${personType[type]}Street`;
    const municipality = `${personType[type]}Municipality`;
    const city = `${personType[type]}City`;
    const cityInSerbia = `${personType[type]}CityInSerbia`;
    const zipCode = `${personType[type]}ZipCode`;
    const phone = `${personType[type]}Phone`;
    const email = `${personType[type]}Email`;

    legalEntityTouched[businessName] = true;
    legalEntityTouched[country] = true;
    legalEntityTouched[vat] = true;
    legalEntityTouched[establishmentDate] = true;
    legalEntityTouched[establishmentPlace] = true;
    legalEntityTouched[name] = true;
    legalEntityTouched[lastName] = true;
    legalEntityTouched[docType] = true;
    legalEntityTouched[docNumber] = true;
    legalEntityTouched[issuePlace] = true;
    legalEntityTouched[issueDate] = true;
    legalEntityTouched[gender] = true;
    legalEntityTouched[street] = true;
    legalEntityTouched[municipality] = true;
    legalEntityTouched[city] = true;
    legalEntityTouched[cityInSerbia] = true;
    legalEntityTouched[zipCode] = true;
    legalEntityTouched[phone] = true;
    legalEntityTouched[email] = true;

    if (!values[businessName]) {
        legalEntityExistingErrors[businessName] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (values[country] === '') {
        legalEntityExistingErrors[country] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (!values[vat] || values[vat] === '') {
        legalEntityExistingErrors[vat] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (!!values.VATFormatError) {
        legalEntityExistingErrors[vat] = translations.validationMessages.VAT_INVALID_FORMAT;
        legalEntityIsValid = false;
    }

    if (!values[establishmentDate]) {
        legalEntityExistingErrors[establishmentDate] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (!values[establishmentPlace]) {
        legalEntityExistingErrors[establishmentPlace] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (!values[name]) {
        legalEntityExistingErrors[name] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (!values[lastName]) {
        legalEntityExistingErrors[lastName] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (!values[docType]) {
        legalEntityExistingErrors[docType] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (!values[docNumber]) {
        legalEntityExistingErrors[docNumber] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (!values[issuePlace]) {
        legalEntityExistingErrors[issuePlace] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (!values[issueDate]) {
        legalEntityExistingErrors[issueDate] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (!values[gender]) {
        legalEntityExistingErrors[gender] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (values[street] === '') {
        legalEntityExistingErrors[street] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (values[municipality] === '') {
        legalEntityExistingErrors[municipality] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (
        values[country] !== constValues.countrySerbiaId.toString(10) &&
        values[country] !== constValues.countrySerbiaId &&
        values[city] === ''
    ) {
        legalEntityExistingErrors[city] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (
        (values[country] === constValues.countrySerbiaId.toString(10) ||
            values[country] === constValues.countrySerbiaId) &&
        (!values[cityInSerbia] || values[cityInSerbia].id === '')
    ) {
        legalEntityExistingErrors[cityInSerbia] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (values[zipCode] === '') {
        legalEntityExistingErrors[zipCode] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (!values[phone] || (values[phone] && values[phone].length < 5)) {
        legalEntityExistingErrors[phone] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (
        (values[phone] && values[phone].length > 4 && !checkPhoneNumber(values[phone])) ||
        (values[phone] &&
            values[phone].length > 4 &&
            values[country] === constValues.countrySerbiaId &&
            !checkIfSerbianPhoneNumber(values[phone]))
    ) {
        legalEntityExistingErrors[phone] = translations.validationMessages.PHONE_NUMBER_FORMAT;
        legalEntityIsValid = false;
    }

    if (!values[email]) {
        legalEntityExistingErrors[email] = translations.validationMessages.REQUIRED_FIELD;
        legalEntityIsValid = false;
    }

    if (values[email] && !checkEmail(values[email])) {
        // check only if there is a value
        legalEntityExistingErrors[email] = translations.validationMessages.EMAIL_ADDRESS_FORMAT;
        legalEntityIsValid = false;
    }

    return { errors: legalEntityExistingErrors, touched: legalEntityTouched, isValid: legalEntityIsValid };
};
